import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { OnboardingHeader, type OnboardingTabKey } from "./OnboardingHeader";
import { SelfiePillar } from "./SelfiePillar";
import { useOnboarding } from "../hooks/use-onboarding";
import defaultCameraIcon from "../assets/camera-icon.png";

interface SelfieOnboardingPageProps {
  onTabChange?: (tab: OnboardingTabKey) => void;
  brandName?: string;
  logoSrc?: string;
  cardLabels?: Record<string, string>;
  focusLabels?: Record<string, string>;
  cameraIconSrc?: string;
  footerSlot?: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function SelfieOnboardingPage({
  onTabChange,
  brandName,
  logoSrc,
  cameraIconSrc = defaultCameraIcon,
  footerSlot,
  title,
  subtitle,
}: SelfieOnboardingPageProps) {
  const { t } = useTranslation();
  const { skipOnboarding, isSkipping } = useOnboarding();

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // clientX ve clientY doğrudan ekrandaki (viewport) konumu verir.
    setMousePos({
      x: e.clientX,
      y: e.clientY,
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        if (typeof mixpanel.track === "function") {
          mixpanel.track("onboarding_viewed", {
            type: "selfie_pillar"
          });
        }
      } catch (e) {
        console.warn("Mixpanel error:", e);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const skipButton = (
    <Button
      variant="outline"
      onClick={() => {
        skipOnboarding();
      }}
      disabled={isSkipping}
      className="border-border-primary text-text-primary hover:bg-bg-secondary hover:text-text-primary transition-colors"
    >
      {t("onboarding.skip")}
    </Button>
  );

  return (
    // 🚀 DÜZELTME 1: En dış kutu ekran boyutuna (h-screen) sabitlenip taşmalar gizlendi (overflow-hidden)
    <div 
      className="relative h-screen w-full bg-bg-primary overflow-hidden z-1001"
      onMouseMove={handleMouseMove}
    >
      {/* 🚀 DÜZELTME 2: Arka plan noktaları bu sabit ekranın (absolute inset-0) arkasına duvar kağıdı gibi yapıştırıldı */}
      <div 
        className="absolute inset-0 pointer-events-none z-0 opacity-20 dark:opacity-10"
        style={{
          backgroundImage: `radial-gradient(currentColor 1px, transparent 1px)`,
          backgroundSize: `24px 24px`
        }}
      />

      <div 
        className="absolute inset-0 pointer-events-none z-0 transition-opacity duration-300"
        style={{
          backgroundImage: `radial-gradient(rgba(59, 130, 246, 0.8) 1px, transparent 1px)`,
          backgroundSize: `24px 24px`,
          WebkitMaskImage: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, black, transparent)`,
          maskImage: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, black, transparent)`
        }}
      />

      {/* 🚀 DÜZELTME 3: ASIL İÇERİK KUTUSU (Scroll burada çalışacak!) */}
      {/* Sadece bu kutu aşağı doğru uzayacak ve kaydırılabilecek (overflow-y-auto) */}
      <div className="relative z-10 h-full w-full overflow-y-auto pb-24">
        <OnboardingHeader
          activeTab="selfie"
          onTabChange={onTabChange}
          brandName={brandName}
          logoSrc={logoSrc}
          rightSlot={skipButton}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 md:pt-12">
          
          {/* Hero Banner */}
          <section className="mb-10 relative overflow-hidden rounded-2xl p-6 sm:p-8 md:p-12 min-h-62.5 md:min-h-87.5 flex items-center border border-border-primary shadow-sm bg-linear-to-r from-cyan-500/10 via-blue-500/5 to-purple-500/10">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-pink-500/20 rounded-full blur-3xl mix-blend-multiply" />
            
            <div className="relative z-10 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-bg-primary/40 backdrop-blur-md border border-border-primary mb-6 shadow-sm animate-glide w-fit">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <span className="text-[10px] sm:text-xs uppercase tracking-widest font-bold text-text-primary">
                  {t("onboarding.newChapter", { defaultValue: "YENİ BÖLÜM KİLİDİ AÇILDI" })}
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 sm:mb-6 leading-tight text-text-primary tracking-tight">
                {title ?? t("onboarding.pageTitle")}
              </h1>
              
              <p className="text-base sm:text-lg mb-2 text-text-secondary leading-relaxed font-medium">
                {subtitle ?? t("onboarding.pageSubtitle")}
              </p>
            </div>
          </section>

          <SelfiePillar
            cameraIconSrc={cameraIconSrc}
            footerSlot={footerSlot}
          />
        </div>
      </div>
    </div>
  );
}